import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "./api";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useParams,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { NotificationProvider } from "./context/NotificationContext";
import FullAdminDashboard from "./components/FullAdminDashboard";
import AdminUserManagement from "./components/AdminUserManagement";
import AdminHome from "./components/AdminHome";
import AdminCalendar from "./components/AdminCalendar";
import EventManagementWrapper from "./components/EventManagementWrapper";
import EventGallery from "./EventGallery";
import EventLandingPage from "./components/EventLandingPage";
import LandingPage from "./components/LandingPage";
import LoginPage from "./components/LoginPage";
import RegisterPage from "./components/RegisterPage";
import EventBook from "./components/EventBook";
import FeaturesPage from "./components/FeaturesPage";
import InvitationView from "./components/InvitationView";
import {
  AfrahPage,
  KhotobaPage,
  KatbKetabPage,
  GraduationPage,
  PrivacyPage,
  TermsPage,
  ContactPage,
  AboutPage
} from "./components/static/StaticPages";

const ProtectedRoute = ({ user, children, roles = [] }) => {
  if (!user) return <Navigate to="/login" replace />;
  if (roles.length > 0 && !roles.includes(user.role))
    return <Navigate to="/" replace />;
  return children;
};


const Navbar = ({ user, handleLogout }) => {
  const location = useLocation();
  const collapseRef = useRef(null);

  useEffect(() => {
    // Close Bootstrap collapse on route change
    const collapseEl = document.getElementById('navbarNav');
    if (collapseEl && collapseEl.classList.contains('show')) {
      const toggler = document.querySelector('.navbar-toggler');
      if (toggler) toggler.click();
    }
  }, [location]);

  return (
    <nav className="navbar navbar-expand-lg fixed-top d-print-none glass-nav">
      <div className="container px-3 px-md-4">
        {/* Brand */}
        <Link className="navbar-brand py-1 fs-3 fs-md-2" to="/" style={{ letterSpacing: '-0.05em' }}>
          يومك
        </Link>

        <button
          className="navbar-toggler border-0 shadow-none"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
        >
          <span className="material-symbols-outlined text-primary">menu</span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav" ref={collapseRef}>
          <ul className="navbar-nav me-lg-5 align-items-center gap-2 gap-lg-4 py-3 py-lg-0">
            <li className="nav-item">
              <Link className="nav-link" to="/">الرئيسية</Link>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="/#events">المناسبات</a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="/#features">المميزات</a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="/#pricing">الأسعار</a>
            </li>
          </ul>

          <div className="ms-auto d-flex flex-column flex-lg-row align-items-center gap-3 mt-lg-0 pb-3 pb-lg-0">
            {user ? (
              <div className="d-flex align-items-center gap-3">
                <Link className="nav-link p-0" to={user.role === 'admin' ? "/admin" : "/client"}>لوحة التحكم</Link>
                <span className="text-on-surface-variant small fw-bold d-none d-md-inline">أهلاً، {user.username}</span>
                <button
                  className="btn btn-primary btn-sm px-4 py-2"
                  onClick={handleLogout}
                >
                  خروج
                </button>
              </div>
            ) : (
              <div className="d-flex flex-column flex-lg-row align-items-center gap-3">
                <Link className="text-primary font-bold text-sm text-decoration-none" to="/login">دخول المشتركين</Link>
                <Link
                  className="btn btn-primary px-4 py-2 text-sm"
                  to="/register"
                >
                  ابدأ ألبومك دلوقتي
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

function App() {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("youmak_user");
    const token = localStorage.getItem("youmak_token");
    if (stored && token) return JSON.parse(stored);
    return null;
  });

  // Performance: Lift events state to prevent redundant fetching and refreshes
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const fetchEvents = useCallback(async (silent = false) => {
    if (!user) return;
    if (!silent) setEventsLoading(true);
    try {
      const endpoint = user.role === 'admin' ? "/api/events/admin/all" : "/api/events/my-events";
      console.log(`Fetching events from: ${endpoint}`);
      const res = await api.get(endpoint);
      console.log(`Fetched ${res.data?.length} events`);
      setEvents(res.data);
      return res.data;
    } catch (err) {
      console.error("Fetch events error", err);
      return [];
    } finally {
      if (!silent) setEventsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && (user.role === 'client' || user.role === 'admin')) {
      fetchEvents();
    }
  }, [user, fetchEvents]);

  const handleLogout = () => {
    localStorage.removeItem("youmak_token");
    localStorage.removeItem("youmak_user");
    setUser(null);
  };

  return (
    <Router>
      <NotificationProvider>
        <AppContent
          user={user}
          setUser={setUser}
          handleLogout={handleLogout}
          events={events}
          eventsLoading={eventsLoading}
          fetchEvents={fetchEvents}
        />
      </NotificationProvider>
    </Router>
  );
}

const AppContent = ({ user, setUser, handleLogout, events, eventsLoading, fetchEvents }) => {
  return (
    <div
      className="app-root min-vh-100 d-flex flex-column"
      dir="rtl"
      style={{ background: "var(--bg)" }}
    >
      <Routes>
        {/* Dashboard Routes - No Global Nav/Footer */}
        <Route
          element={
            <ProtectedRoute user={user} roles={["client", "admin"]}>
              <FullAdminDashboard
                user={user}
                events={events}
                onLogout={handleLogout}
              />
            </ProtectedRoute>
          }
        >
          <Route path="/client" element={<AdminHome user={user} events={events} loading={eventsLoading} fetchEvents={fetchEvents} />} />
          <Route path="/client/event/:eventId" element={<EventManagementWrapper events={events} fetchEvents={fetchEvents} />} />
          {/* Admin specific within dashboard */}
          <Route path="/admin" element={
            <ProtectedRoute user={user} roles={["admin"]}>
              <AdminHome user={user} events={events} loading={eventsLoading} fetchEvents={fetchEvents} />
            </ProtectedRoute>
          } />
          <Route path="/admin/calendar" element={
            <ProtectedRoute user={user} roles={["admin"]}>
              <AdminCalendar events={events} />
            </ProtectedRoute>
          } />
          <Route path="/admin/event/:eventId" element={
            <ProtectedRoute user={user} roles={["admin"]}>
              <EventManagementWrapper events={events} fetchEvents={fetchEvents} />
            </ProtectedRoute>
          } />
        </Route>

        {/* Super Admin Console - No Global Nav/Footer */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute user={user} roles={["admin"]}>
              <AdminUserManagement onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        {/* Global Layout Routes */}
        <Route element={<GlobalLayout user={user} handleLogout={handleLogout} />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/afrah" element={<AfrahPage />} />
          <Route path="/khotoba" element={<KhotobaPage />} />
          <Route path="/katb-ketab" element={<KatbKetabPage />} />
          <Route path="/graduation" element={<GraduationPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route
            path="/login"
            element={<LoginPage onLogin={(u) => setUser(u)} />}
          />
          <Route path="/register" element={<RegisterPage onLogin={(u) => setUser(u)} />} />
          <Route path="/event/:eventId" element={<EventLandingPage />} />
          <Route
            path="/event-book/:eventId"
            element={
              <ProtectedRoute user={user}>
                <EventBook />
              </ProtectedRoute>
            }
          />
          <Route path="/event/:eventId/invitation" element={<InvitationView />} />
        </Route>
      </Routes>
    </div>
  );
};

const GlobalLayout = ({ user, handleLogout }) => {
  return (
    <>
      <Navbar user={user} handleLogout={handleLogout} />
      <main className="container-fluid p-0 pt-5 flex-grow-1">
        <Outlet />
      </main>
      <footer className="footer pt-5 pb-4 mt-auto d-print-none">
          <div className="container px-4">
            <div className="row g-4 mb-5">
              <div className="col-lg-4">
                <div className="text-3xl font-black text-primary mb-4" style={{ fontFamily: 'var(--font-headline)', fontSize: '1.875rem' }}>يومك</div>
                <p className="text-on-surface-variant arabic-leading mb-4">
                  بنشارككم فرحتكم ونوثق أحلى لحظات العمر بأحدث تكنولوجيا المشاركة الفورية. يومك.. ذكرياتكم في أمان وبأعلى جودة.
                </p>
                <div className="d-flex gap-2">
                  <a href="https://www.facebook.com/youmak.egp/" target="_blank" rel="noreferrer" className="bg-white p-2 rounded-3 text-primary transition-soft border border-primary-dim shadow-none text-decoration-none"><span className="material-symbols-outlined">share</span></a>
                  <a href="mailto:youmak.egp@gmail.com" className="bg-white p-2 rounded-3 text-primary transition-soft border border-primary-dim shadow-none text-decoration-none"><span className="material-symbols-outlined">alternate_email</span></a>
                </div>
              </div>
              <div className="col-lg-2 col-md-4">
                <h5 className="font-bold mb-4 text-primary">مناسباتنا</h5>
                <ul className="list-unstyled space-y-2 text-on-surface-variant fw-medium">
                  <li><Link to="/afrah" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">الأفراح (Afrah)</Link></li>
                  <li><Link to="/khotoba" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">الخطوبة (Khotoba)</Link></li>
                  <li><Link to="/katb-ketab" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">كتب الكتاب</Link></li>
                  <li><Link to="/graduation" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">حفلات التخرج</Link></li>
                </ul>
              </div>
              <div className="col-lg-2 col-md-4">
                <h5 className="font-bold mb-4 text-primary">روابط سريعة</h5>
                <ul className="list-unstyled space-y-2 text-on-surface-variant fw-medium">
                  <li><Link to="/privacy" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">سياسة الخصوصية</Link></li>
                  <li><Link to="/terms" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">الشروط والأحكام</Link></li>
                  <li><Link to="/contact" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">اتصل بنا</Link></li>
                  <li><Link to="/about" className="text-decoration-none text-on-surface-variant hover:text-primary transition-soft">عن الشركة</Link></li>
                </ul>
              </div>
              <div className="col-lg-4 col-md-4">
                <div className="bg-white p-4 rounded-celebration shadow-sm border border-primary-dim">
                  <h5 className="font-bold mb-3 text-primary">تواصل معنا</h5>
                  <p className="small text-on-surface-variant mb-4 arabic-leading">فريقنا متاح دايماً لمساعدتك في تخطيط ذكريات فرحك.</p>
                  <a className="text-primary font-black d-block mb-2 text-decoration-none" href="mailto:youmak.egp@gmail.com">youmak.egp@gmail.com</a>
                  <p className="text-primary font-bold">01013201998</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-top border-primary-dim text-center">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                <p className="text-on-surface-variant opacity-60 small mb-0">
                  © {new Date().getFullYear()} جميع الحقوق محفوظة.
                  تطوير بواسطة <a href="https://mohamed-reda-swe-mohamedreda22s-projects.vercel.app/" target="_blank" rel="noreferrer" className="text-primary text-decoration-none fw-bold">Mohamed Reda Web Solutions ⚡</a>
                </p>
                <div className="bg-white px-4 py-2 rounded-pill shadow-sm border border-primary-dim">
                  <span className="text-primary fw-bold">صنع بكل حب لذكريات مصرية أصيلة ❤️</span>
                </div>
              </div>
            </div>
          </div>
      </footer>
    </>
  );
};

export default App;
